import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import axios from 'axios';

const OrgPlaidSettings = ({ org, onUpdate }) => {
  const [formData, setFormData] = useState({
    plaidClientIdDev: '',
    plaidSecretDev: '',
    plaidEnvDev: 'sandbox',
    plaidClientIdProd: '',
    plaidSecretProd: '',
    plaidEnvProd: 'production'
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [showSecretDev, setShowSecretDev] = useState(false);
  const [showSecretProd, setShowSecretProd] = useState(false);

  useEffect(() => {
    if (org) {
      setFormData({
        plaidClientIdDev: org.plaidClientIdDev || '',
        plaidSecretDev: org.plaidSecretDev || '',
        plaidEnvDev: org.plaidEnvDev || 'sandbox',
        plaidClientIdProd: org.plaidClientIdProd || '',
        plaidSecretProd: org.plaidSecretProd || '',
        plaidEnvProd: org.plaidEnvProd || 'production'
      });
    }
  }, [org]);

  const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      const res = await axios.put(`/api/organizations/${org._id}`, formData);
      setStatus({ type: 'success', message: 'Plaid credentials saved successfully.' });
      if (onUpdate) onUpdate(res.data);
    } catch (err) {
      console.error(err);
      setStatus({ type: 'danger', message: err.response?.data?.msg || 'Failed to update Plaid credentials.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="org-plaid-settings">
      <div className="mb-4">
        <h4 className="font-weight-bold text-dark h5 mb-1">Plaid Multi-Environment Keys</h4>
        <p className="text-muted small mb-0">
          Configure API credentials separately for local developer machines and cloud deployment.
        </p>
      </div>

      {status && <Alert variant={status.type} className="border-0 shadow-sm mb-4">{status.message}</Alert>}

      <Form onSubmit={onSubmit}>
        <Row>
          {/* Local Development Credentials */}
          <Col md={6} className="mb-4">
            <Card className="shadow-sm border-0 h-100">
              <Card.Body>
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-light-blue p-2 rounded-circle mr-3 text-primary d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                    <i className="fas fa-laptop-code"></i>
                  </div>
                  <div>
                    <h5 className="font-weight-bold mb-0" style={{ fontSize: '0.95rem' }}>Local Development (Dev)</h5>
                    <small className="text-muted">Used on localhost / local database machines.</small>
                  </div>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label className="small font-weight-bold">Dev Client ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="plaidClientIdDev"
                    value={formData.plaidClientIdDev}
                    onChange={onChange}
                    size="sm"
                    placeholder="e.g. 65dad21e5c87..."
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small font-weight-bold">Dev Secret</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showSecretDev ? 'text' : 'password'}
                      name="plaidSecretDev"
                      value={formData.plaidSecretDev}
                      onChange={onChange}
                      size="sm"
                      placeholder="••••••••••••••••••••••••"
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    />
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={() => setShowSecretDev(!showSecretDev)}
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0 }}
                    >
                      <i className={`fas ${showSecretDev ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </Button>
                  </div>
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label className="small font-weight-bold">Dev Environment</Form.Label>
                  <Form.Control
                    as="select"
                    name="plaidEnvDev"
                    value={formData.plaidEnvDev}
                    onChange={onChange}
                    size="sm"
                  >
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="development">Development (Test with Real accounts - 10 Item Limit)</option>
                    <option value="production">Production (Live)</option>
                  </Form.Control>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          {/* Production Credentials */}
          <Col md={6} className="mb-4">
            <Card className="shadow-sm border-0 h-100">
              <Card.Body>
                <div className="d-flex align-items-center mb-3">
                  <div className="bg-light-green p-2 rounded-circle mr-3 text-success d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                    <i className="fas fa-cloud-upload-alt"></i>
                  </div>
                  <div>
                    <h5 className="font-weight-bold mb-0" style={{ fontSize: '0.95rem' }}>Cloud Server (Production)</h5>
                    <small className="text-muted">Used on the live web portal.</small>
                  </div>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label className="small font-weight-bold">Prod Client ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="plaidClientIdProd"
                    value={formData.plaidClientIdProd}
                    onChange={onChange}
                    size="sm"
                    placeholder="e.g. 65dad21e5c87..."
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small font-weight-bold">Prod Secret</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showSecretProd ? 'text' : 'password'}
                      name="plaidSecretProd"
                      value={formData.plaidSecretProd}
                      onChange={onChange}
                      size="sm"
                      placeholder="••••••••••••••••••••••••"
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    />
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={() => setShowSecretProd(!showSecretProd)}
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0 }}
                    >
                      <i className={`fas ${showSecretProd ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </Button>
                  </div>
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label className="small font-weight-bold">Prod Environment</Form.Label>
                  <Form.Control
                    as="select"
                    name="plaidEnvProd"
                    value={formData.plaidEnvProd}
                    onChange={onChange}
                    size="sm"
                  >
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="development">Development (Test with Real accounts - 10 Item Limit)</option>
                    <option value="production">Production (Live)</option>
                  </Form.Control>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="text-right">
          <Button type="submit" variant="primary" size="sm" disabled={loading} className="px-4 py-2 font-weight-bold">
            {loading ? 'Saving API Keys...' : 'Save Multi-Environment Keys'}
          </Button>
        </div>
      </Form>

      <style dangerouslySetInnerHTML={{ __html: `
        .bg-light-blue { background-color: #e0f2fe; }
        .bg-light-green { background-color: #dcfce7; }
      `}} />
    </div>
  );
};

export default OrgPlaidSettings;
